import { EXPRESS_STRIPE_LAYOUT, expressColumns } from './express-layout.js';

const controllers = new WeakMap();

/** Shared native-provider readiness, independent of the page's loading trigger. */
export function initExpressLoading(root) {
    if (!root) return;
    if (controllers.has(root)) return controllers.get(root);
    const status = root.querySelector('[data-express-loading-status]');
    const heading = root.querySelector('[data-express-heading]');
    const label = status?.querySelector?.('[data-express-loading-label]');
    const defaultLoadingLabel = label?.textContent || 'Express-Zahlarten werden geladen …';
    const methods = root.querySelector('[data-express-methods]');
    const fallback = root.querySelector('[data-express-unavailable]');
    const fallbackMessage =
        fallback?.querySelector('[data-express-unavailable-message]') || fallback;
    const defaultFallback = fallbackMessage?.textContent || '';
    const providers = [...root.querySelectorAll('[data-express-provider]')]
        .map((slot) => ({
            slot,
            host: slot.querySelector(
                '[data-product-paypal], [data-paypal-checkout], [data-product-stripe], [data-stripe-checkout]',
            ),
            placeholder: slot.querySelector('[data-express-placeholder]'),
            pending: true,
            nativePending: false,
            failed: false,
            preserveControls: false,
        }))
        .filter((provider) => provider.host);
    // Eligibility is unknown before the interaction/SDK gate. Start with the
    // configured wallet row and More, rather than treating every offer as visible.
    let stripeCount;
    let stripeHeight = 0;
    const stripeHost = providers
        .find((provider) => provider.slot.getAttribute('data-express-provider') === 'stripe')
        ?.host.querySelector('[data-stripe-express]');
    let width = 0;
    let frame;

    function sync() {
        let loading = false;
        let available = false;
        let updating = false;
        providers.forEach((provider) => {
            const { host, slot, placeholder } = provider;
            if (slot.getAttribute('data-express-provider') === 'paypal') {
                const controls = host.querySelectorAll?.('[data-paypal-funding]:not([hidden])');
                if (controls?.length) {
                    [
                        ...placeholder.querySelector('[data-express-placeholder-buttons]').children,
                    ].forEach((button, index) => {
                        button.hidden = index >= controls.length;
                    });
                }
            }
            const rendering =
                !host.hidden &&
                (host.style.visibility === 'hidden' ||
                    host.hasAttribute('data-stripe-express-rendering'));
            const checking =
                !provider.failed && (provider.pending || provider.nativePending || rendering);
            const waiting = checking && !(provider.preserveControls && !host.hidden && !rendering);
            updating ||= checking && !waiting;
            loading ||= checking;
            available ||= !host.hidden;
            if (placeholder && placeholder.hidden === waiting) placeholder.hidden = !waiting;
            slot.toggleAttribute('data-express-provider-loading', waiting);
            slot.toggleAttribute('data-express-provider-updating', checking && !waiting);
            slot.toggleAttribute('inert', checking);
            slot.hidden = !waiting && host.hidden;
        });
        root.toggleAttribute(
            'data-express-loading',
            loading && !root.hasAttribute('data-express-deferred'),
        );
        root.toggleAttribute('data-express-updating', updating);
        if (heading) heading.hidden = updating;
        status?.classList?.toggle('u-visually-hidden', !updating);
        if (label) {
            const text = updating ? 'Betrag wird aktualisiert …' : defaultLoadingLabel;
            if (label.textContent !== text) label.textContent = text;
        }
        if (loading && !root.hasAttribute('data-express-deferred'))
            root.setAttribute('aria-busy', 'true');
        else root.removeAttribute('aria-busy');
        if (status) status.hidden = !loading;
        if (fallback) {
            const reasons = [
                ...new Set(
                    providers
                        .filter(({ host }) => host.hidden)
                        .map(({ host }) =>
                            host
                                .querySelector('[data-paypal-error], [data-stripe-error]')
                                ?.textContent.trim(),
                        )
                        .filter(Boolean),
                ),
            ];
            const text = reasons.length
                ? 'Express Checkout ist für diese Auswahl nicht verfügbar: ' + reasons.join(' ')
                : defaultFallback;
            if (fallbackMessage.textContent !== text) fallbackMessage.textContent = text;
            fallback.hidden = loading || available;
        }
        root.hidden = !loading && !available && (!fallback || fallback.hidden);
    }

    function updateColumns() {
        const buttons = root.querySelector(
            '[data-express-provider="stripe"] [data-express-placeholder-buttons]',
        );
        if (!buttons || !(width > 0)) return;
        const gap = Number.parseFloat(getComputedStyle(methods).columnGap) || 8;
        const columns = expressColumns(width, gap, stripeCount);
        const visibleCount = Math.min(
            stripeCount ?? Infinity,
            columns * EXPRESS_STRIPE_LAYOUT.maxRows,
        );
        const more = buttons
            .closest('[data-express-placeholder]')
            .querySelector('.z-express-payments__placeholder-more');
        // Native branding can move offers into More even when the requested
        // columns fit. Prefer actual closed-widget geometry once delivered;
        // single-row gutters and temporary SDK viewports are not a More row.
        const nativeMore =
            stripeHeight >= 43 && stripeHeight <= 100 ? stripeHeight >= 76 : undefined;
        if (more)
            more.hidden =
                EXPRESS_STRIPE_LAYOUT.overflow === 'never' ||
                !(nativeMore ?? (stripeCount === undefined || stripeCount > visibleCount));
        buttons.style.setProperty('--z-express-columns', String(columns));
        [...buttons.children].forEach((button, index) => {
            button.hidden = index >= visibleCount;
        });
    }

    const observer = new MutationObserver(() => {
        if (!root.isConnected) {
            observer.disconnect();
            resizeObserver?.disconnect();
            return;
        }
        sync();
    });
    providers.forEach(({ host }) =>
        observer.observe(host, {
            attributes: true,
            attributeFilter: ['hidden', 'style', 'data-stripe-express-rendering'],
            childList: true,
            subtree: true,
        }),
    );
    observer.observe(root, { attributes: true, attributeFilter: ['data-express-deferred'] });
    const resizeObserver =
        typeof ResizeObserver === 'function' && methods
            ? new ResizeObserver((entries) => {
                  for (const entry of entries) {
                      if (entry.target === methods) width = entry.contentRect.width;
                      else if (entry.target === stripeHost) {
                          const frame = stripeHost.querySelector('iframe');
                          const box = entry.borderBoxSize?.[0] || entry.borderBoxSize;
                          stripeHeight =
                              (box?.blockSize ?? entry.contentRect.height) +
                              Math.min(0, Number.parseFloat(frame?.style.marginTop) || 0) +
                              Math.min(0, Number.parseFloat(frame?.style.marginBottom) || 0);
                      }
                  }
                  cancelAnimationFrame(frame);
                  frame = requestAnimationFrame(updateColumns);
              })
            : null;
    resizeObserver?.observe(methods);
    if (stripeHost) resizeObserver?.observe(stripeHost, { box: 'border-box' });
    root.addEventListener('stripe-advanced:express-methods', (event) => {
        if (!providers.some((provider) => event.target === provider.host)) return;
        stripeCount = Math.max(1, Number(event.detail?.count) || 1);
        updateColumns();
    });
    const resetStripe = (host) => {
        if (host.querySelector('[data-stripe-express]')) {
            stripeCount = undefined;
            stripeHeight = 0;
            updateColumns();
        }
    };
    const change = (host, pending, failed) => {
        const provider = providers.find((entry) => entry.host === host);
        if (!provider) return;
        provider.pending = pending;
        provider.preserveControls = false;
        if (failed !== undefined) provider.failed = failed;
        if (failed) provider.nativePending = false;
        sync();
    };
    const controller = {
        start: (host) => {
            resetStripe(host);
            change(host, true, false);
        },
        finish: (host) => change(host, false),
        fail: (host) => change(host, false, true),
        nativeStart: (host) => {
            resetStripe(host);
            const provider = providers.find((entry) => entry.host === host);
            if (provider) {
                provider.nativePending = true;
                provider.failed = false;
                provider.preserveControls = false;
            }
            sync();
        },
        nativeFinish: (host) => {
            const provider = providers.find((entry) => entry.host === host);
            if (provider) provider.nativePending = false;
            sync();
        },
        startAll: ({ preserveAvailable = false } = {}) => {
            providers.forEach((provider) => {
                const { host } = provider;
                provider.preserveControls =
                    preserveAvailable &&
                    !provider.failed &&
                    !host.hidden &&
                    host.style.visibility !== 'hidden' &&
                    !host.hasAttribute('data-stripe-express-rendering') &&
                    Boolean(
                        host.querySelector('[data-stripe-express], [data-paypal-express]')
                            ?.childElementCount,
                    );
                if (!provider.preserveControls) resetStripe(host);
                provider.pending = true;
                provider.failed = false;
            });
            sync();
        },
        failAll: () => {
            providers.forEach((provider) => {
                provider.pending = false;
                provider.failed = true;
                provider.nativePending = false;
                provider.preserveControls = false;
            });
            sync();
        },
        sync,
    };
    controllers.set(root, controller);
    sync();
    return controller;
}
